import { Fragment } from 'react';
import {
    Room as RoomIcon,
    Cloud as CloudIcon,
    Assignment as AssignmentIcon,
    Storage as StorageIcon,
    SettingsEthernet as SettingsEthernetIcon,
    Poll as PollIcon,
    Timeline as TimelineIcon,
    Email as EmailIcon,
    Build as BuildIcon,
    VerifiedUser as VerifiedUserIcon,
    Phone as PhoneIcon,
    SignalWifi4Bar as SignalWiFi4BarIcon,
    VpnKey as VpnKeyIcon,
    Settings as SettingsIcon,
    CheckCircle as CheckCircleIcon,
    Person as UserIcon,
    Security as SecurityIcon,
    Sync as SyncIcon,
    SettingsSystemDaydream as SettingsSystemDaydreamIcon,
    Group as GroupIcon,
    Dashboard as DashboardIcon,
    BorderStyle as WiredIcon,
    LocalHospital as LocalHospitalIcon,
} from '@material-ui/icons';
import { t } from '@lingui/macro';

import { NPoint2K, VmwareVm, Plc, LegacyIcon, ISNG } from '../../icon';
import { MenuGroup, MenuGroupItem } from '../menu';
import { AdminPages, buildRootPageTarget } from '../../pages-admin/AdminLinkUtils';
import { useIsSysAdminUser } from '../../../stores/SessionStore';
import { useIsSelf } from '../../../utilities/PgmHooks';
import { useIsIpmDisabled, useIsSEMEnabled, useActiveStandbyRole } from '../../../stores/EnvironmentStore';

export default function PlmAdminMenu() {
    const agentSettingsIcon = 'ipm-icons-agent3k';
    const [isSysAdminUser] = useIsSysAdminUser();
    const isSelf = useIsSelf();
    const [isIpmDisabled] = useIsIpmDisabled();
    const [isSEMEnabled] = useIsSEMEnabled();
    const [activeStandbyRole] = useActiveStandbyRole();
    const isActiveStandbyServer =
        activeStandbyRole === 'active' || activeStandbyRole === 'standby' || activeStandbyRole === 'maintenance';

    return (
        <>
            <MenuGroup id="enterprise" title="Enterprise" expanded>
                <MenuGroupItem
                    id={AdminPages.Sites}
                    title={t`Sites`}
                    icon={<RoomIcon />}
                    highlightChildLinks
                    link={{
                        target: buildRootPageTarget(AdminPages.Sites),
                    }}
                />
                <MenuGroupItem
                    id={AdminPages.BusinessServices}
                    title={t`Business Services`}
                    icon={<CloudIcon />}
                    highlightChildLinks
                    link={{
                        target: buildRootPageTarget(AdminPages.BusinessServices),
                    }}
                />
            </MenuGroup>
            <MenuGroup id="monitoring" title={t`Monitoring`} expanded>
                <MenuGroupItem
                    id={AdminPages.Agents}
                    title={t`nPoints`}
                    icon={<NPoint2K />}
                    highlightChildLinks
                    link={{
                        target: buildRootPageTarget(AdminPages.Agents),
                    }}
                />
                <MenuGroupItem
                    id={AdminPages.ServiceTests}
                    title={t`Service Tests`}
                    icon={<AssignmentIcon />}
                    highlightChildLinks
                    link={{
                        target: buildRootPageTarget(AdminPages.ServiceTests),
                    }}
                />
                {!isIpmDisabled && (
                    <Fragment key="ipm-fragment">
                        <MenuGroupItem
                            id={AdminPages.MonitoredInfrastructure}
                            title={t`Monitored Infrastructure`}
                            highlightChildLinks
                            icon={<StorageIcon />}
                            link={{
                                target: buildRootPageTarget(AdminPages.MonitoredInfrastructure),
                            }}
                        />
                        <MenuGroupItem
                            id={AdminPages.VirtualInfrastructure}
                            title={t`VMware® Infrastructure`}
                            highlightChildLinks
                            icon={<VmwareVm />}
                            link={{
                                target: buildRootPageTarget(AdminPages.VirtualInfrastructure),
                            }}
                        />
                        <MenuGroupItem
                            id={AdminPages.Interfaces}
                            title={t`Interfaces`}
                            icon={<SettingsEthernetIcon />}
                            highlightChildLinks
                            link={{
                                target: buildRootPageTarget(AdminPages.Interfaces),
                            }}
                        />
                        <MenuGroupItem
                            id={AdminPages.Polling}
                            title={t`Polling`}
                            icon={<PollIcon />}
                            highlightChildLinks
                            link={{
                                target: buildRootPageTarget(AdminPages.Polling),
                            }}
                        />
                    </Fragment>
                )}
                <MenuGroupItem
                    id={AdminPages.Thresholds}
                    title={t`Thresholds`}
                    icon={<TimelineIcon />}
                    highlightChildLinks
                    link={{
                        target: buildRootPageTarget(AdminPages.Thresholds),
                    }}
                />
                <MenuGroupItem
                    id={AdminPages.Notifications}
                    title={t`Notifications`}
                    icon={<EmailIcon />}
                    highlightChildLinks
                    link={{
                        target: buildRootPageTarget(AdminPages.Notifications),
                    }}
                />
                {!isIpmDisabled && (
                    <MenuGroupItem
                        id={AdminPages.Syslog}
                        title={t`Syslog Collection`}
                        icon={<BuildIcon />}
                        highlightChildLinks
                        link={{
                            target: buildRootPageTarget(AdminPages.Syslog),
                        }}
                    />
                )}
            </MenuGroup>
            <MenuGroup id="system" title={t`System`} expanded>
                {isSelf && (
                    <MenuGroupItem
                        id={AdminPages.Users}
                        title={t`Users`}
                        icon={<UserIcon />}
                        highlightChildLinks
                        link={{
                            target: buildRootPageTarget(AdminPages.Users),
                        }}
                    />
                )}
                <MenuGroupItem
                    id={AdminPages.Groups}
                    title={t`Groups`}
                    icon={<GroupIcon />}
                    highlightChildLinks
                    link={{
                        target: buildRootPageTarget(AdminPages.Groups),
                    }}
                />
                <MenuGroupItem
                    id={AdminPages.Dashboards}
                    title={t`Dashboards`}
                    icon={<DashboardIcon />}
                    highlightChildLinks
                    link={{
                        target: buildRootPageTarget(AdminPages.Dashboards),
                    }}
                />
                <MenuGroupItem
                    id={AdminPages.AgentSettings}
                    title={t`nPoint Settings`}
                    icon={<LegacyIcon icon={agentSettingsIcon} />}
                    link={{
                        target: buildRootPageTarget(AdminPages.AgentSettings),
                    }}
                />
                <MenuGroupItem
                    id={AdminPages.AgentCertificates}
                    title={t`Certificates`}
                    icon={<VerifiedUserIcon />}
                    highlightChildLinks
                    link={{
                        target: buildRootPageTarget(AdminPages.AgentCertificates),
                    }}
                />
                <MenuGroupItem
                    id={AdminPages.VoipConfig}
                    title={t`VoIP Configuration`}
                    icon={<PhoneIcon />}
                    highlightChildLinks
                    link={{
                        target: buildRootPageTarget(AdminPages.VoipConfig),
                    }}
                />
                <MenuGroupItem
                    id={AdminPages.WifiNetworks}
                    title={t`Wi-Fi Networks`}
                    icon={<SignalWiFi4BarIcon />}
                    highlightChildLinks
                    link={{
                        target: buildRootPageTarget(AdminPages.WifiNetworks),
                    }}
                />
                <MenuGroupItem
                    id={AdminPages.WiredNetworks}
                    title={t`Wired Networks`}
                    icon={<WiredIcon />}
                    highlightChildLinks
                    link={{
                        target: buildRootPageTarget(AdminPages.WiredNetworks),
                    }}
                />
                <MenuGroupItem
                    id={AdminPages.ApiTokens}
                    title={t`API Tokens`}
                    icon={<VpnKeyIcon />}
                    link={{
                        target: buildRootPageTarget(AdminPages.ApiTokens),
                    }}
                />
                {isSysAdminUser && (
                    <MenuGroupItem
                        id={AdminPages.Sso}
                        title={t`Authentication`}
                        icon={<SecurityIcon />}
                        highlightChildLinks
                        link={{
                            target: buildRootPageTarget(AdminPages.Sso),
                        }}
                    />
                )}
                {isActiveStandbyServer && (
                    <MenuGroupItem
                        id={AdminPages.ActiveStandbyPlms}
                        title={t`Active/Standby`}
                        icon={<SyncIcon />}
                        highlightChildLinks
                        link={{
                            target: buildRootPageTarget(AdminPages.ActiveStandbyPlms),
                        }}
                    />
                )}
                <MenuGroupItem
                    id={AdminPages.MaintenanceWindows}
                    title={t`Maintenance Windows`}
                    icon={<SettingsSystemDaydreamIcon />}
                    highlightChildLinks
                    link={{
                        target: buildRootPageTarget(AdminPages.MaintenanceWindows),
                    }}
                />
                {!isIpmDisabled && (
                    <MenuGroupItem
                        id={AdminPages.Collectors}
                        title={t`Collectors`}
                        icon={<Plc />}
                        highlightChildLinks
                        link={{
                            target: buildRootPageTarget(AdminPages.Collectors),
                        }}
                    />
                )}
                {isSEMEnabled && (
                    <MenuGroupItem
                        id={AdminPages.ISNGs}
                        title={t`ISNGs`}
                        icon={<ISNG />}
                        highlightChildLinks
                        link={{
                            target: buildRootPageTarget(AdminPages.ISNGs),
                        }}
                    />
                )}
                <MenuGroupItem
                    id={AdminPages.ServerHealth}
                    title={t`Server Health`}
                    icon={<LocalHospitalIcon />}
                    link={{
                        target: buildRootPageTarget(AdminPages.ServerHealth),
                    }}
                />
                <MenuGroupItem
                    id={AdminPages.AuditActivities}
                    title={t`Audit Activities`}
                    icon={<AssignmentIcon />}
                    link={{
                        target: buildRootPageTarget(AdminPages.AuditActivities),
                    }}
                />
                {isSysAdminUser && (
                    <MenuGroupItem
                        id={AdminPages.General}
                        title={t`General`}
                        icon={<SettingsIcon />}
                        link={{
                            target: buildRootPageTarget(AdminPages.General),
                        }}
                    />
                )}
                <MenuGroupItem
                    id={AdminPages.Licensing}
                    title={t`Licensing`}
                    icon={<CheckCircleIcon />}
                    link={{
                        target: buildRootPageTarget(AdminPages.Licensing),
                    }}
                />
            </MenuGroup>
        </>
    );
}
